import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { PlusCircle, Pencil, Trash2, Search, Building2, Phone, Mail, MapPin, User, FileText, Lock } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;
const api = axios.create({ baseURL: `${API}/api` });
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

const VENDOR_CATEGORIES = [
  'Plumbing', 'Electrical', 'HVAC', 'Landscaping', 'Painting',
  'Roofing', 'Cleaning', 'Security', 'Pest Control', 'General Maintenance',
  'Pool Service', 'Elevator', 'Fire Safety', 'Flooring', 'Other'
];

const emptyForm = {
  company_name: '', contact_person: '', email: '', phone: '',
  address: '', service_type: '', categories: [], notes: '', status: 'active',
  enable_portal: false,
  agreement_type: '', response_time: '', service_schedule: '',
  rate_type: '', rate_amount: '', sla_terms: '',
  contract_start: '', contract_end: ''
};

const VendorManager = ({ apiPrefix = '/hoa' }) => {
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingVendor, setEditingVendor] = useState(null);
  const [form, setForm] = useState({ ...emptyForm });
  const [saving, setSaving] = useState(false);
  const [showDetail, setShowDetail] = useState(null);
  const [portalStatus, setPortalStatus] = useState(null);
  const [portalLoading, setPortalLoading] = useState(false);
  const [invoices, setInvoices] = useState([]);

  const fetchVendors = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get(`${apiPrefix}/vendors`);
      setVendors(res.data.vendors || []);
    } catch (err) {
      console.error('Error fetching vendors:', err);
    } finally {
      setLoading(false);
    }
  }, [apiPrefix]);

  useEffect(() => { fetchVendors(); }, [fetchVendors]);

  const openCreate = () => {
    setEditingVendor(null);
    setForm({ ...emptyForm });
    setPortalStatus(null);
    setShowForm(true);
  };

  const openEdit = (vendor) => {
    setEditingVendor(vendor);
    setForm({
      company_name: vendor.company_name || '',
      contact_person: vendor.contact_person || '',
      email: vendor.email || '',
      phone: vendor.phone || '',
      address: vendor.address || '',
      service_type: vendor.service_type || '',
      categories: vendor.categories || [],
      notes: vendor.notes || '',
      status: vendor.status || 'active',
      enable_portal: vendor.portal_access || false,
      agreement_type: vendor.agreement_type || '',
      response_time: vendor.response_time || '',
      service_schedule: vendor.service_schedule || '',
      rate_type: vendor.rate_type || '',
      rate_amount: vendor.rate_amount || '',
      sla_terms: vendor.sla_terms || '',
      contract_start: vendor.contract_start || '',
      contract_end: vendor.contract_end || ''
    });
    setPortalStatus(null);
    // Load portal status
    if (vendor.id) {
      api.get(`/vendors/${vendor.id}/portal-status`).then(res => {
        setPortalStatus(res.data);
      }).catch(() => setPortalStatus(null));
    }
    setShowForm(true);
  };

  const openDetail = async (vendor) => {
    setShowDetail(vendor);
    try {
      const res = await api.get(`/vendor-portal/invoices?vendor_email=${vendor.email}`);
      setInvoices(res.data.invoices || []);
    } catch {
      setInvoices([]);
    }
  };

  const handleSave = async () => {
    if (!form.company_name.trim()) {
      toast.error('Vendor/Company Name is required');
      return;
    }
    setSaving(true);
    try {
      let vendorId = editingVendor?.id;
      const { enable_portal, ...vendorData } = form;
      
      if (editingVendor) {
        await api.patch(`${apiPrefix}/vendors/${editingVendor.id}`, vendorData);
        toast.success('Vendor updated');
      } else {
        const res = await api.post(`${apiPrefix}/vendors`, vendorData);
        vendorId = res.data.vendor?.id;
        toast.success('Vendor added');
      }
      
      // Enable portal access if toggled on and vendor has email
      if (enable_portal && vendorId && form.email) {
        const currentPortal = portalStatus?.portal_status;
        if (!currentPortal || currentPortal === 'not_enabled' || currentPortal === 'disabled') {
          try {
            const portalRes = await api.post(`/vendors/${vendorId}/portal-access`);
            if (portalRes.data.already_registered) {
              toast.info('Vendor already has a portal account — linked to your organization');
            } else {
              toast.success('Portal invitation sent to ' + form.email);
            }
          } catch (portalErr) {
            toast.error('Vendor saved, but portal invitation failed: ' + (portalErr.response?.data?.detail || 'Unknown error'));
          }
        }
      }
      
      setShowForm(false);
      setForm({ ...emptyForm });
      setEditingVendor(null);
      setPortalStatus(null);
      fetchVendors();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save vendor');
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivate = async (vendor) => {
    if (!window.confirm(`Deactivate ${vendor.company_name}?`)) return;
    try {
      await api.delete(`${apiPrefix}/vendors/${vendor.id}`);
      toast.success('Vendor deactivated');
      fetchVendors();
    } catch (err) {
      toast.error('Failed to deactivate vendor');
    }
  };

  const handlePortalAction = async (action, vendor) => {
    if (!vendor?.id) return;
    setPortalLoading(true);
    try {
      const res = await api.post(`/vendors/${vendor.id}/${action}`);
      toast.success(res.data.message);
      // Refresh portal status
      const statusRes = await api.get(`/vendors/${vendor.id}/portal-status`);
      setPortalStatus(statusRes.data);
      fetchVendors();
    } catch (err) {
      toast.error(err.response?.data?.detail || `Action failed`);
    } finally {
      setPortalLoading(false);
    }
  };

  const toggleCategory = (cat) => {
    setForm(prev => ({
      ...prev,
      categories: prev.categories.includes(cat)
        ? prev.categories.filter(c => c !== cat)
        : [...prev.categories, cat]
    }));
  };

  const filtered = vendors.filter(v => {
    const q = search.toLowerCase();
    return !q || (v.company_name || '').toLowerCase().includes(q)
      || (v.contact_person || '').toLowerCase().includes(q)
      || (v.email || '').toLowerCase().includes(q)
      || (v.service_type || '').toLowerCase().includes(q);
  });

  return (
    <Card>
      <CardHeader className="p-4 sm:p-6">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
          <CardTitle className="text-lg sm:text-xl" data-testid="vendor-directory-title">Vendor Directory</CardTitle>
          <Button className="bg-emerald-600 hover:bg-emerald-700 text-sm" size="sm"
            onClick={openCreate} data-testid="add-vendor-btn">
            <PlusCircle className="mr-2 h-4 w-4" />
            Add Vendor
          </Button>
        </div>
        <div className="relative mt-3">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input placeholder="Search vendors..." value={search} onChange={e => setSearch(e.target.value)}
            className="pl-9" data-testid="vendor-search" />
        </div>
      </CardHeader>
      <CardContent className="p-4 sm:p-6 pt-0">
        {loading ? (
          <p className="text-center text-gray-500 py-8">Loading vendors...</p>
        ) : filtered.length === 0 ? (
          <div className="text-center py-12 text-gray-500" data-testid="no-vendors">
            <Building2 className="h-12 w-12 mx-auto mb-3 text-gray-300" />
            <p className="font-medium">No vendors found</p>
            <p className="text-sm mt-1">Click "Add Vendor" to create your first vendor</p>
          </div>
        ) : (
          <div className="space-y-3">
            {filtered.map(vendor => (
              <div key={vendor.id} className="p-4 border rounded-lg hover:bg-gray-50 transition-colors cursor-pointer"
                onClick={() => openDetail(vendor)} data-testid={`vendor-row-${vendor.id}`}>
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-2">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h4 className="font-semibold text-sm sm:text-base">{vendor.company_name}</h4>
                      {vendor.portal_access && (
                        <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700">Portal Access</Badge>
                      )}
                    </div>
                    {vendor.contact_person && (
                      <p className="text-xs sm:text-sm text-gray-600 flex items-center gap-1 mt-1">
                        <User className="h-3 w-3" /> {vendor.contact_person}
                      </p>
                    )}
                    <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1 text-xs text-gray-500">
                      {vendor.email && <span className="flex items-center gap-1"><Mail className="h-3 w-3" /> {vendor.email}</span>}
                      {vendor.phone && <span className="flex items-center gap-1"><Phone className="h-3 w-3" /> {vendor.phone}</span>}
                    </div>
                    <div className="flex gap-1.5 mt-2 flex-wrap">
                      {vendor.agreement_type && (
                        <Badge className={vendor.agreement_type === 'sla' ? 'bg-amber-100 text-amber-800' : vendor.agreement_type === 'retainer' ? 'bg-purple-100 text-purple-800' : 'bg-slate-100 text-slate-700'}>
                          {vendor.agreement_type === 'sla' ? 'SLA' : vendor.agreement_type === 'a_la_carte' ? 'A la carte' : vendor.agreement_type === 'retainer' ? 'Retainer' : vendor.agreement_type}
                        </Badge>
                      )}
                      {(vendor.categories || []).map((cat, i) => (
                        <Badge key={i} variant="outline" className="text-xs">{cat}</Badge>
                      ))}
                      {vendor.service_type && !vendor.categories?.length && (
                        <Badge variant="outline" className="text-xs capitalize">{vendor.service_type.replace('_', ' ')}</Badge>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className={vendor.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}>
                      {vendor.status === 'active' ? 'Active' : 'Inactive'}
                    </Badge>
                    <Button variant="ghost" size="sm" onClick={e => { e.stopPropagation(); openEdit(vendor); }}
                      data-testid={`edit-vendor-${vendor.id}`}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                    {vendor.status === 'active' && (
                      <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700"
                        onClick={e => { e.stopPropagation(); handleDeactivate(vendor); }}
                        data-testid={`deactivate-vendor-${vendor.id}`}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>

      {/* Add/Edit Vendor Modal */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle data-testid="vendor-form-title">
              {editingVendor ? 'Edit Vendor' : 'Add New Vendor'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label>Vendor / Company Name *</Label>
              <Input value={form.company_name} onChange={e => setForm({ ...form, company_name: e.target.value })}
                placeholder="e.g., Island Plumbing Co." data-testid="vendor-company-name" />
            </div>
            <div>
              <Label>Contact Person</Label>
              <Input value={form.contact_person} onChange={e => setForm({ ...form, contact_person: e.target.value })}
                placeholder="e.g., John Smith" data-testid="vendor-contact-person" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Email</Label>
                <Input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })}
                  placeholder="vendor@email.com" data-testid="vendor-email" />
              </div>
              <div>
                <Label>Phone</Label>
                <Input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })}
                  placeholder="+1 (242) 555-0123" data-testid="vendor-phone" />
              </div>
            </div>
            <div>
              <Label>Address</Label>
              <Input value={form.address} onChange={e => setForm({ ...form, address: e.target.value })}
                placeholder="Street, City, State" data-testid="vendor-address" />
            </div>
            <div>
              <Label>Service Type</Label>
              <Select value={form.service_type} onValueChange={v => setForm({ ...form, service_type: v })}>
                <SelectTrigger data-testid="vendor-service-type">
                  <SelectValue placeholder="Select service type" />
                </SelectTrigger>
                <SelectContent>
                  {VENDOR_CATEGORIES.map(cat => (
                    <SelectItem key={cat} value={cat.toLowerCase().replace(/\s+/g, '_')}>{cat}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Service Categories (select all that apply)</Label>
              <div className="flex flex-wrap gap-2 mt-1">
                {VENDOR_CATEGORIES.map(cat => (
                  <Badge key={cat} variant={form.categories.includes(cat) ? 'default' : 'outline'}
                    className={`cursor-pointer text-xs ${form.categories.includes(cat) ? 'bg-emerald-600' : ''}`}
                    onClick={() => toggleCategory(cat)}>
                    {cat}
                  </Badge>
                ))}
              </div>
            </div>
            <div>
              <Label>Notes</Label>
              <textarea className="w-full px-3 py-2 border rounded-md text-sm resize-none" rows={3}
                value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })}
                placeholder="Optional notes about the vendor..." data-testid="vendor-notes" />
            </div>

            {/* Service Agreement / SLA Section */}
            <div className="border-t pt-4 mt-2">
              <h4 className="font-semibold text-sm mb-3 flex items-center gap-2">
                <FileText className="h-4 w-4" /> Service Agreement
              </h4>
              <div className="space-y-3">
                <div>
                  <Label>Agreement Type</Label>
                  <Select value={form.agreement_type} onValueChange={v => setForm({ ...form, agreement_type: v })}>
                    <SelectTrigger data-testid="vendor-agreement-type">
                      <SelectValue placeholder="Select agreement type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="sla">SLA (Contracted)</SelectItem>
                      <SelectItem value="a_la_carte">A la carte (On-demand)</SelectItem>
                      <SelectItem value="retainer">Retainer</SelectItem>
                      <SelectItem value="project">Project-based</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {(form.agreement_type === 'sla' || form.agreement_type === 'retainer') && (
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>Contract Start</Label>
                      <Input type="date" value={form.contract_start}
                        onChange={e => setForm({ ...form, contract_start: e.target.value })}
                        data-testid="vendor-contract-start" />
                    </div>
                    <div>
                      <Label>Contract End</Label>
                      <Input type="date" value={form.contract_end}
                        onChange={e => setForm({ ...form, contract_end: e.target.value })}
                        data-testid="vendor-contract-end" />
                    </div>
                  </div>
                )}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Response Time</Label>
                    <Select value={form.response_time} onValueChange={v => setForm({ ...form, response_time: v })}>
                      <SelectTrigger data-testid="vendor-response-time">
                        <SelectValue placeholder="Select" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="same_day">Same Day</SelectItem>
                        <SelectItem value="24hrs">24 Hours</SelectItem>
                        <SelectItem value="48hrs">48 Hours</SelectItem>
                        <SelectItem value="72hrs">72 Hours</SelectItem>
                        <SelectItem value="1_week">1 Week</SelectItem>
                        <SelectItem value="as_scheduled">As Scheduled</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Service Schedule</Label>
                    <Select value={form.service_schedule} onValueChange={v => setForm({ ...form, service_schedule: v })}>
                      <SelectTrigger data-testid="vendor-service-schedule">
                        <SelectValue placeholder="Select" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="weekly">Weekly</SelectItem>
                        <SelectItem value="biweekly">Bi-weekly</SelectItem>
                        <SelectItem value="monthly">Monthly</SelectItem>
                        <SelectItem value="quarterly">Quarterly</SelectItem>
                        <SelectItem value="on_call">On-call</SelectItem>
                        <SelectItem value="as_needed">As Needed</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Rate Type</Label>
                    <Select value={form.rate_type} onValueChange={v => setForm({ ...form, rate_type: v })}>
                      <SelectTrigger data-testid="vendor-rate-type">
                        <SelectValue placeholder="Select" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="hourly">Hourly</SelectItem>
                        <SelectItem value="fixed">Fixed</SelectItem>
                        <SelectItem value="per_visit">Per Visit</SelectItem>
                        <SelectItem value="monthly_retainer">Monthly Retainer</SelectItem>
                        <SelectItem value="project">Per Project</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Rate Amount ($)</Label>
                    <Input type="number" step="0.01" value={form.rate_amount}
                      onChange={e => setForm({ ...form, rate_amount: e.target.value })}
                      placeholder="0.00" data-testid="vendor-rate-amount" />
                  </div>
                </div>
                <div>
                  <Label>SLA Terms / Contract Notes</Label>
                  <textarea className="w-full px-3 py-2 border rounded-md text-sm resize-none" rows={2}
                    value={form.sla_terms} onChange={e => setForm({ ...form, sla_terms: e.target.value })}
                    placeholder="Key contract terms, penalties, renewal conditions..." data-testid="vendor-sla-terms" />
                </div>
              </div>
            </div>

            <div>
              <Label>Status</Label>
              <Select value={form.status} onValueChange={v => setForm({ ...form, status: v })}>
                <SelectTrigger data-testid="vendor-status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Vendor Portal Access Panel */}
            <div className="border-t pt-4 mt-2">
              <h4 className="font-semibold text-sm mb-3 flex items-center gap-2">
                <Lock className="h-4 w-4" /> Vendor Portal Access
              </h4>
              
              {editingVendor && portalStatus && portalStatus.portal_status !== 'not_enabled' ? (
                <div className="space-y-3 bg-gray-50 rounded-lg p-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Portal Status</span>
                    <Badge className={
                      portalStatus.portal_status === 'active' ? 'bg-green-100 text-green-800' :
                      portalStatus.portal_status === 'invitation_pending' ? 'bg-yellow-100 text-yellow-800' :
                      portalStatus.portal_status === 'disabled' || portalStatus.portal_status === 'suspended' ? 'bg-red-100 text-red-800' :
                      'bg-gray-100 text-gray-600'
                    }>
                      {portalStatus.portal_status === 'active' ? 'Active' :
                       portalStatus.portal_status === 'invitation_pending' ? 'Invitation Pending' :
                       portalStatus.portal_status === 'pending_approval' ? 'Pending Approval' :
                       portalStatus.portal_status === 'suspended' ? 'Disabled' :
                       portalStatus.portal_status}
                    </Badge>
                  </div>
                  {portalStatus.portal_email && (
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-gray-500">Portal Email</span>
                      <span>{portalStatus.portal_email}</span>
                    </div>
                  )}
                  {portalStatus.invited_at && (
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-gray-500">Invitation Sent</span>
                      <span>{new Date(portalStatus.invited_at).toLocaleDateString()}</span>
                    </div>
                  )}
                  {portalStatus.last_login && (
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-gray-500">Last Login</span>
                      <span>{new Date(portalStatus.last_login).toLocaleString()}</span>
                    </div>
                  )}
                  <div className="flex gap-2 flex-wrap pt-1">
                    {portalStatus.portal_status === 'invitation_pending' && (
                      <Button size="sm" variant="outline" disabled={portalLoading}
                        onClick={() => handlePortalAction('portal-resend', editingVendor)}>
                        Resend Invitation
                      </Button>
                    )}
                    {portalStatus.portal_status === 'active' && (
                      <Button size="sm" variant="outline" disabled={portalLoading}
                        onClick={() => handlePortalAction('portal-reset-password', editingVendor)}>
                        Send Password Reset
                      </Button>
                    )}
                    {(portalStatus.portal_status === 'active' || portalStatus.portal_status === 'invitation_pending') && (
                      <Button size="sm" variant="outline" className="text-red-600 hover:text-red-700" disabled={portalLoading}
                        onClick={() => handlePortalAction('portal-disable', editingVendor)}>
                        Disable Portal Access
                      </Button>
                    )}
                    {(portalStatus.portal_status === 'disabled' || portalStatus.portal_status === 'suspended') && (
                      <Button size="sm" variant="outline" className="text-emerald-600" disabled={portalLoading}
                        onClick={() => handlePortalAction('portal-access', editingVendor)}>
                        Re-enable Portal Access
                      </Button>
                    )}
                  </div>
                </div>
              ) : (
                <div className="flex items-center gap-3 bg-gray-50 rounded-lg p-3">
                  <input type="checkbox" id="enable-portal" checked={form.enable_portal}
                    onChange={e => setForm({ ...form, enable_portal: e.target.checked })}
                    className="h-4 w-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                    data-testid="enable-portal-checkbox" />
                  <label htmlFor="enable-portal" className="text-sm">
                    <span className="font-medium">Enable Vendor Portal Access</span>
                    <p className="text-xs text-gray-500 mt-0.5">
                      {form.email ? `An invitation will be sent to ${form.email}` : 'Enter an email above to enable portal access'}
                    </p>
                  </label>
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
            <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={handleSave} disabled={saving}
              data-testid="vendor-save-btn">
              {saving ? 'Saving...' : editingVendor ? 'Save Changes' : 'Add Vendor'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Vendor Detail Modal */}
      <Dialog open={!!showDetail} onOpenChange={() => setShowDetail(null)}>
        <DialogContent className="sm:max-w-xl max-h-[90vh] overflow-y-auto">
          {showDetail && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Building2 className="h-5 w-5" />
                  {showDetail.company_name}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-2">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  {showDetail.contact_person && (
                    <div><span className="text-gray-500">Contact:</span> <span className="font-medium">{showDetail.contact_person}</span></div>
                  )}
                  {showDetail.email && (
                    <div><span className="text-gray-500">Email:</span> <span className="font-medium">{showDetail.email}</span></div>
                  )}
                  {showDetail.phone && (
                    <div><span className="text-gray-500">Phone:</span> <span className="font-medium">{showDetail.phone}</span></div>
                  )}
                  {showDetail.address && (
                    <div className="col-span-2"><span className="text-gray-500">Address:</span> <span className="font-medium">{showDetail.address}</span></div>
                  )}
                  <div><span className="text-gray-500">Status:</span>{' '}
                    <Badge className={showDetail.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100'}>
                      {showDetail.status}
                    </Badge>
                  </div>
                  {showDetail.portal_access && (
                    <div><span className="text-gray-500">Portal:</span> <Badge className="bg-blue-100 text-blue-800">Has Portal Access</Badge></div>
                  )}
                </div>
                {showDetail.notes && (
                  <div className="text-sm"><span className="text-gray-500">Notes:</span> <p className="mt-1">{showDetail.notes}</p></div>
                )}
                <div className="flex flex-wrap gap-1.5">
                  {(showDetail.categories || []).map((c, i) => <Badge key={i} variant="outline">{c}</Badge>)}
                </div>

                {/* Service Agreement Details */}
                {showDetail.agreement_type && (
                  <div className="border-t pt-4">
                    <h4 className="font-semibold flex items-center gap-2 mb-3 text-sm"><FileText className="h-4 w-4" /> Service Agreement</h4>
                    <div className="grid grid-cols-2 gap-3 text-sm bg-amber-50 rounded-lg p-4 border border-amber-200">
                      <div>
                        <span className="text-gray-500">Agreement:</span>
                        <p className="font-medium capitalize">{showDetail.agreement_type === 'sla' ? 'SLA (Contracted)' : showDetail.agreement_type === 'a_la_carte' ? 'A la carte' : showDetail.agreement_type === 'retainer' ? 'Retainer' : showDetail.agreement_type}</p>
                      </div>
                      {showDetail.response_time && (
                        <div><span className="text-gray-500">Response Time:</span><p className="font-medium capitalize">{showDetail.response_time.replace(/_/g, ' ')}</p></div>
                      )}
                      {showDetail.service_schedule && (
                        <div><span className="text-gray-500">Schedule:</span><p className="font-medium capitalize">{showDetail.service_schedule.replace(/_/g, ' ')}</p></div>
                      )}
                      {showDetail.rate_type && (
                        <div><span className="text-gray-500">Rate:</span><p className="font-medium capitalize">{showDetail.rate_amount ? `$${Number(showDetail.rate_amount).toLocaleString()} ` : ''}{showDetail.rate_type.replace(/_/g, ' ')}</p></div>
                      )}
                      {showDetail.contract_start && (
                        <div><span className="text-gray-500">Contract Period:</span><p className="font-medium">{showDetail.contract_start}{showDetail.contract_end ? ` — ${showDetail.contract_end}` : ' — Ongoing'}</p></div>
                      )}
                      {showDetail.sla_terms && (
                        <div className="col-span-2"><span className="text-gray-500">Terms:</span><p className="mt-1 text-xs">{showDetail.sla_terms}</p></div>
                      )}
                    </div>
                  </div>
                )}

                {/* Invoice History */}
                <div className="border-t pt-4">
                  <h4 className="font-semibold flex items-center gap-2 mb-3"><FileText className="h-4 w-4" /> Invoice History</h4>
                  {invoices.length > 0 ? (
                    <div className="space-y-2">
                      {invoices.map((inv, i) => (
                        <div key={i} className="p-3 border rounded-lg text-sm flex justify-between items-center">
                          <div>
                            <p className="font-medium">{inv.invoice_number || `INV-${(i+1).toString().padStart(3,'0')}`}</p>
                            <p className="text-xs text-gray-500">{inv.description || inv.service_description || 'Invoice'}</p>
                            <p className="text-xs text-gray-400">{inv.created_at ? new Date(inv.created_at).toLocaleDateString() : ''}</p>
                          </div>
                          <div className="text-right">
                            <p className="font-semibold">${(inv.total || inv.total_amount || inv.amount || inv.subtotal || 0).toLocaleString()}</p>
                            <Badge variant="outline" className={
                              inv.status === 'approved' || inv.status === 'paid' ? 'bg-green-50 text-green-700' :
                              inv.status === 'pending' ? 'bg-yellow-50 text-yellow-700' :
                              inv.status === 'rejected' ? 'bg-red-50 text-red-700' : ''
                            }>
                              {inv.status || 'pending'}
                            </Badge>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500 text-center py-4">No invoices on record</p>
                  )}
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => openEdit(showDetail)}>
                  <Pencil className="h-4 w-4 mr-2" /> Edit Vendor
                </Button>
                <Button variant="outline" onClick={() => setShowDetail(null)}>Close</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default VendorManager;
